import React from 'react';
import { Link } from 'react-router-dom';

const ImportSelection = () => {
  const importOptions = [
    {
      id: 'items',
      title: 'Items',
      description: 'Import products, services, and inventory items',
      icon: '📦',
      color: 'blue',
      route: '/import-data',
      features: ['Item details', 'Pricing', 'Stock levels', 'Categories', 'Barcodes', 'HSN codes']
    },
    {
      id: 'parties',
      title: 'Parties',
      description: 'Import customers, suppliers, and business contacts',
      icon: '👥',
      color: 'green',
      route: '/import-parties',
      features: ['Contact details', 'Addresses', 'GST information', 'Opening balances', 'Party types']
    },
    {
      id: 'sales',
      title: 'Sales Invoices',
      description: 'Import sales transactions and invoices',
      icon: '📄',
      color: 'green',
      route: '/import-sales',
      features: ['Invoice numbers', 'Customer details', 'Amounts', 'Payment status', 'Dates']
    },
    {
      id: 'purchases',
      title: 'Purchase Bills',
      description: 'Import purchase transactions and bills',
      icon: '📦',
      color: 'orange',
      route: '/import-purchases',
      features: ['Bill numbers', 'Supplier details', 'Amounts', 'Payment status', 'Dates']
    },
    {
      id: 'expenses',
      title: 'Expenses',
      description: 'Import business expenses and costs',
      icon: '💰',
      color: 'purple',
      route: '/import-expenses',
      features: ['Expense heads', 'Amounts', 'Payment modes', 'Categories', 'Employee tracking']
    }
  ];

  const getColorClasses = (color) => {
    const colorMap = {
      blue: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        text: 'text-blue-800',
        button: 'bg-blue-600 hover:bg-blue-700',
        icon: 'text-blue-500'
      },
      green: {
        bg: 'bg-green-50',
        border: 'border-green-200',
        text: 'text-green-800',
        button: 'bg-green-600 hover:bg-green-700',
        icon: 'text-green-500'
      },
      orange: {
        bg: 'bg-orange-50',
        border: 'border-orange-200',
        text: 'text-orange-800',
        button: 'bg-orange-600 hover:bg-orange-700',
        icon: 'text-orange-500'
      },
      purple: {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
        text: 'text-purple-800',
        button: 'bg-purple-600 hover:bg-purple-700',
        icon: 'text-purple-500'
      }
    };
    return colorMap[color] || colorMap.blue;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">Bulk Data Import</h1>
          <p className="text-base sm:text-xl text-gray-600 max-w-3xl mx-auto px-1">
            Choose what type of data you want to import from Excel. Each import type has specific field mappings 
            optimized for that data structure.
          </p>
        </div>

        {/* Import Options Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">
          {importOptions.map((option) => {
            const colors = getColorClasses(option.color);
            return (
              <div
                key={option.id}
                className={`${colors.bg} ${colors.border} border-2 rounded-xl p-5 sm:p-8 hover:shadow-lg transition-all duration-300 sm:transform sm:hover:-translate-y-1`}
              >
                {/* Icon and Title */}
                <div className="text-center mb-6">
                  <div className={`text-6xl mb-4 ${colors.icon}`}>
                    {option.icon}
                  </div>
                  <h3 className={`text-2xl font-bold ${colors.text} mb-2`}>
                    {option.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {option.description}
                  </p>
                </div>

                {/* Features List */}
                <div className="mb-6">
                  <h4 className={`font-semibold ${colors.text} mb-3`}>
                    Supported Fields:
                  </h4>
                  <ul className="space-y-2">
                    {option.features.map((feature, index) => (
                      <li key={index} className="flex items-center text-sm text-gray-700">
                        <svg className={`w-4 h-4 mr-2 ${colors.icon}`} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Action Button */}
                <div className="text-center">
                  <Link
                    to={option.route}
                    className={`inline-flex items-center px-6 py-3 ${colors.button} text-white font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-${option.color}-500`}
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    Import {option.title}
                  </Link>
                </div>
              </div>
            );
          })}
        </div>

        {/* Help Section */}
        <div className="mt-16 bg-white rounded-xl shadow-lg p-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Need Help with Import?</h2>
            <p className="text-gray-600">
              Follow these steps to successfully import your data
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-blue-600">1</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Prepare Your Excel File</h3>
              <p className="text-gray-600 text-sm">
                Ensure your Excel file has headers in the first row and data in subsequent rows. 
                Remove any empty rows or columns.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-green-600">2</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Map Your Columns</h3>
              <p className="text-gray-600 text-sm">
                Our system will auto-detect common field patterns, but you can manually map 
                your Excel columns to the appropriate fields.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-purple-600">3</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Review & Import</h3>
              <p className="text-gray-600 text-sm">
                Preview your mapped data, make any final edits, and import your data 
                with duplicate handling options.
              </p>
            </div>
          </div>
        </div>

        {/* Tips Section */}
        <div className="mt-12 bg-yellow-50 border border-yellow-200 rounded-xl p-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-semibold text-yellow-800 mb-2">Pro Tips for Successful Import</h3>
              <ul className="text-yellow-700 text-sm space-y-1">
                <li>• Use consistent date formats (DD/MM/YYYY or MM/DD/YYYY)</li>
                <li>• Ensure required fields are not empty (marked with *)</li>
                <li>• Use proper number formats for amounts and quantities</li>
                <li>• Check for duplicate data before importing</li>
                <li>• Save a backup of your original Excel file</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportSelection;
